// AiChatWidget — floating, context-aware chat surface.
//
// Summon with Cmd+/ (Mac) or Ctrl+/ (Windows). Always-on floating button
// bottom-right of the viewport opens a 380x540 panel anchored to that corner.
//
// Wires to four callables in functions/src/api/chatAi.js:
//   - chatAi({ thread_id?, messages, page_context? })
//   - listChatThreads()
//   - getChatThread({ thread_id })
//   - deleteChatThread({ thread_id })
//
// Slash commands (parsed locally, then forwarded as the user turn):
//   /draft <topic>      — "Draft a cold email about <topic>." (returns body only)
//   /critique           — "Critique this Dispatch body and give 3 fixes."
//   /intel <name>       — "Pull intel on <name> and suggest one opener angle."
//
// `useDispatchContext` is read off a window-scoped ref so the Dispatch page
// can publish its current subject/body without us reaching into routing
// internals or coupling components. The page sets
//   window.__phantom_dispatch_ctx = { subject, body }
// from a useEffect; we read it here at send-time.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, getDocs, limit as fsLimit, orderBy, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase.js';
import { useAuth } from '../hooks/useAuth.js';

// Panel sizing per spec.
const PANEL_W = 380;
const PANEL_H = 540;

// ── Slash-command parser ────────────────────────────────────────────────────
// Returns the resolved user turn (string) plus any inline metadata used by
// the page-context builder. Unknown commands are passed through verbatim so
// the model can still respond — we never silently swallow the operator's
// input.
export function parseSlashCommand(raw, { dispatchCtx } = {}) {
  const text = String(raw || '').trim();
  if (!text.startsWith('/')) {
    return { kind: 'free', prompt: text };
  }
  // Match `/cmd` optionally followed by whitespace + arg payload.
  const m = text.match(/^\/([a-zA-Z][\w-]*)\s*(.*)$/s);
  if (!m) return { kind: 'free', prompt: text };

  const cmd = m[1].toLowerCase();
  const arg = (m[2] || '').trim();

  switch (cmd) {
    case 'draft': {
      if (!arg) {
        return {
          kind: 'error',
          prompt: text,
          error: 'Usage: /draft <topic>. Example: /draft NAB owner unhappy with current processor',
        };
      }
      return {
        kind: 'draft',
        prompt: `Draft a short cold email body for an NAB merchant-services lead about: ${arg}. Return JUST the body — no subject line, no preamble, under 90 words.`,
      };
    }
    case 'critique': {
      const subject = (dispatchCtx?.subject || '').trim();
      const body = (dispatchCtx?.body || '').trim();
      if (!subject && !body) {
        return {
          kind: 'error',
          prompt: text,
          error: '/critique only works on the Dispatch page once a subject + body are filled in.',
        };
      }
      const sample = body.slice(0, 1200);
      return {
        kind: 'critique',
        prompt:
          'Critique this Dispatch email and list 3 concrete fixes (deliverability, hook, CTA).\n' +
          `\nSubject: ${subject}\n\nBody:\n${sample}`,
      };
    }
    case 'intel': {
      if (!arg) {
        return {
          kind: 'error',
          prompt: text,
          error: 'Usage: /intel <contact name or email>',
        };
      }
      return {
        kind: 'intel',
        prompt:
          `Pull what you can infer about "${arg}" as a B2B prospect for North American Bancard merchant-services leads. ` +
          'Suggest one tailored opener angle. Be honest about what you do not know.',
      };
    }
    default:
      return { kind: 'unknown', prompt: text };
  }
}

// ── Page-context builder ────────────────────────────────────────────────────
// Stays small; the model gets just enough to answer "what am I looking at?"
// without us blasting kilobytes of state per turn.
async function buildPageContext({ pathname, uid, dispatchCtx }) {
  const ctx = { page: pathname || '/' };
  try {
    if (pathname === '/' || pathname === '') {
      const snap = await getDocs(query(
        collection(db, 'events'),
        where('owner_uid', '==', uid),
        orderBy('occurred_at', 'desc'),
        fsLimit(5),
      ));
      ctx.last_events = snap.docs.map((d) => {
        const e = d.data() || {};
        return {
          type: e.type || e.event_type || 'event',
          contact: e.contact_email || e.recipient_email || null,
          campaign: e.campaign_id || null,
          ms: Number(e.occurred_at_ms) || null,
        };
      });
    } else if (pathname.startsWith('/dispatch')) {
      const subject = (dispatchCtx?.subject || '').trim();
      const body = (dispatchCtx?.body || '').trim();
      ctx.dispatch = {
        subject: subject.slice(0, 200),
        body_preview: body.slice(0, 500),
        selected_count: Number(dispatchCtx?.selectedCount) || 0,
      };
    } else if (pathname.startsWith('/contacts')) {
      // Cheap: just owner-scoped count + filter hint if the page set one.
      const filter = (typeof window !== 'undefined' && window.__phantom_contacts_filter) || null;
      ctx.contacts = { filter, total: null };
      try {
        const snap = await getDocs(query(
          collection(db, 'contacts'),
          where('owner_uid', '==', uid),
          fsLimit(1000),
        ));
        ctx.contacts.total = snap.size;
      } catch {
        // best-effort; leave total null
      }
    }
    // Other pages: page name only.
  } catch (e) {
    console.warn('[chat] page context build failed', e?.message);
  }
  return ctx;
}

// ── Token-count formatter ───────────────────────────────────────────────────
function fmtTokens(n) {
  if (!n) return '0';
  if (n < 1000) return String(n);
  return `${(n / 1000).toFixed(1)}k`;
}

// ── Component ───────────────────────────────────────────────────────────────
export function AiChatWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const uid = user?.uid;

  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState(null); // { id, messages: [...] } | null
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [tokenTotal, setTokenTotal] = useState(0);
  const [model, setModel] = useState('llama-3.3-70b');
  const [lastThreadId, setLastThreadId] = useState(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('pt_chat_last_thread') || null;
  });

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Cmd+/ or Ctrl+/ toggles the panel.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Auto-focus the input when panel opens.
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 40);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Auto-scroll to the bottom on new messages.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread, sending]);

  const messages = thread?.messages || [];

  const newThread = useCallback(() => {
    setThread(null);
    setError(null);
    setTokenTotal(0);
    setInput('');
    setTimeout(() => inputRef.current?.focus(), 30);
  }, []);

  const continueLast = useCallback(async () => {
    if (!lastThreadId) return;
    setError(null);
    try {
      const { data } = await httpsCallable(functions, 'getChatThread')({ thread_id: lastThreadId });
      const t = data?.thread;
      if (!t) {
        setError('Last thread not found.');
        return;
      }
      setThread({ id: t.id, messages: Array.isArray(t.messages) ? t.messages : [] });
      if (t.last_usage?.total_tokens) setTokenTotal(Number(t.last_usage.total_tokens) || 0);
      if (t.last_usage?.model) setModel(String(t.last_usage.model).split('/').pop());
    } catch (e) {
      console.warn('[chat] continue last failed', e?.message);
      setError('Could not load last thread.');
    }
  }, [lastThreadId]);

  const send = useCallback(async (rawText) => {
    if (!uid) {
      setError('Sign in required.');
      return;
    }
    const text = String(rawText ?? input).trim();
    if (!text || sending) return;

    // Pull live Dispatch context if available.
    const dispatchCtx = (typeof window !== 'undefined' && window.__phantom_dispatch_ctx) || null;
    const parsed = parseSlashCommand(text, { dispatchCtx });
    if (parsed.kind === 'error') {
      setError(parsed.error);
      return;
    }

    const userMessage = { role: 'user', content: text };
    const promptForModel = parsed.prompt;

    // Optimistic: show the user's exact typed text in the bubble (incl. the
    // slash command), but ship the resolved prompt to the model.
    const localMessages = [...messages, userMessage];
    setThread((prev) => ({
      id: prev?.id || null,
      messages: localMessages,
    }));
    setInput('');
    setSending(true);
    setError(null);

    try {
      const page_context = await buildPageContext({
        pathname: location.pathname,
        uid,
        dispatchCtx,
      });

      // The model sees the resolved prompt, not the literal "/draft …".
      const modelTurns = [
        ...messages,
        { role: 'user', content: promptForModel },
      ];

      const { data } = await httpsCallable(functions, 'chatAi')({
        thread_id: thread?.id || undefined,
        messages: modelTurns,
        page_context,
      });

      const assistant = data?.message;
      if (!assistant?.content) {
        throw new Error('Empty assistant response');
      }
      const newId = data?.thread_id || thread?.id || null;
      setThread({
        id: newId,
        messages: [...localMessages, assistant],
      });
      if (newId) {
        setLastThreadId(newId);
        try { localStorage.setItem('pt_chat_last_thread', newId); } catch { /* ignore */ }
      }
      const tt = Number(data?.usage?.total_tokens) || 0;
      setTokenTotal((prev) => prev + tt);
      if (data?.usage?.model) setModel(String(data.usage.model).split('/').pop());
    } catch (e) {
      console.warn('[chat] send failed', e);
      const msg = e?.message || 'Send failed.';
      setError(msg.replace(/^FirebaseError:\s*/, ''));
      // Roll the optimistic user message back so they can retry.
      setThread((prev) => prev ? { ...prev, messages: messages } : null);
      setInput(text);
    } finally {
      setSending(false);
    }
  }, [uid, input, sending, messages, thread, location.pathname]);

  const onKeyDownInput = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  // Render nothing entirely when the user isn't signed in — the widget is
  // a logged-in surface and the whole shell is gated upstream anyway. Belt
  // and braces in case it's ever mounted outside the gate.
  if (!user) return null;

  return (
    <>
      {/* Floating launcher — visible whether the panel is open or not. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close AI chat' : 'Open AI chat (Cmd+/)'}
        title={open ? 'Close (Esc)' : 'AI chat — Cmd+/ or Ctrl+/'}
        style={{
          position: 'fixed',
          right: 18,
          bottom: 18,
          zIndex: 70,
          width: 44,
          height: 44,
          borderRadius: 999,
          border: '1px solid var(--rule)',
          background: 'var(--btn-bg)',
          color: 'var(--btn-fg)',
          fontSize: 18,
          fontWeight: 700,
          letterSpacing: '0.06em',
          boxShadow: '0 6px 18px rgba(0,0,0,0.28)',
          cursor: 'pointer',
        }}
      >
        {open ? '×' : 'AI'}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="AI assistant"
          onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
          style={{
            position: 'fixed',
            right: 18,
            bottom: 72,
            width: PANEL_W,
            height: PANEL_H,
            maxWidth: 'calc(100vw - 36px)',
            maxHeight: 'calc(100vh - 96px)',
            zIndex: 70,
            background: 'var(--card)',
            border: '1px solid var(--rule)',
            boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
            display: 'flex',
            flexDirection: 'column',
            color: 'var(--ink)',
          }}
        >
          {/* Header */}
          <div
            className="px-3 py-2 flex items-center gap-2"
            style={{ borderBottom: '1px solid var(--rule)' }}
          >
            <div className="text-[9px] uppercase tracking-[0.22em]" style={{ color: 'var(--muted)' }}>
              Assistant
            </div>
            <div className="text-[11px] font-semibold tracking-tight flex-1 min-w-0 truncate" style={{ color: 'var(--ink)' }}>
              {thread?.id ? 'Conversation' : 'New thread'}
            </div>
            <button
              type="button"
              onClick={newThread}
              className="text-[9px] uppercase tracking-[0.18em] px-2 py-1"
              style={{
                border: '1px solid var(--rule)',
                background: 'transparent',
                color: 'var(--muted)',
              }}
              title="Start a new thread"
            >
              New
            </button>
            {lastThreadId && (!thread || thread.id !== lastThreadId) && (
              <button
                type="button"
                onClick={continueLast}
                className="text-[9px] uppercase tracking-[0.18em] px-2 py-1"
                style={{
                  border: '1px solid var(--rule)',
                  background: 'transparent',
                  color: 'var(--muted)',
                }}
                title="Reload your most recent conversation"
              >
                Continue last
              </button>
            )}
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="text-[12px] px-2 py-1"
              style={{
                border: '1px solid var(--rule)',
                background: 'transparent',
                color: 'var(--muted)',
              }}
            >
              ×
            </button>
          </div>

          {/* Slash-command hint strip */}
          <div
            className="px-3 py-1.5 text-[10px]"
            style={{
              borderBottom: '1px solid var(--rule)',
              color: 'var(--muted)',
              background: 'var(--sunken, transparent)',
            }}
          >
            <span style={{ opacity: 0.85 }}>
              <code>/draft</code> · <code>/critique</code> · <code>/intel</code>
            </span>
            <span style={{ float: 'right', opacity: 0.7 }}>{location.pathname}</span>
          </div>

          {/* Conversation */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-3 py-3"
            style={{ background: 'var(--card)' }}
          >
            {messages.length === 0 && (
              <div className="text-[11px]" style={{ color: 'var(--muted)' }}>
                Ask anything about your campaigns, or use a slash command to draft, critique, or pull intel. The model sees the page you are on.
              </div>
            )}
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role} content={m.content} />
            ))}
            {sending && (
              <Bubble role="assistant" content="…" pending />
            )}
            {error && (
              <div
                className="mt-2 px-2 py-1.5 text-[10px]"
                style={{
                  border: '1px solid var(--rule)',
                  color: 'var(--negative, #c34)',
                  background: 'var(--sunken, transparent)',
                }}
              >
                {error}
              </div>
            )}
          </div>

          {/* Composer */}
          <div
            className="px-2 py-2"
            style={{ borderTop: '1px solid var(--rule)', background: 'var(--card)' }}
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDownInput}
              rows={2}
              placeholder="Ask, or try /draft, /critique, /intel…"
              disabled={sending}
              className="w-full px-2 py-1.5 text-[12px]"
              style={{
                background: 'var(--sunken, transparent)',
                color: 'var(--ink)',
                border: '1px solid var(--rule)',
                outline: 'none',
                resize: 'none',
                fontFamily: 'inherit',
              }}
            />
            <div className="flex items-center justify-between mt-1.5">
              <div className="text-[9px] uppercase tracking-[0.18em]" style={{ color: 'var(--muted)' }}>
                <span>{model}</span>
                <span style={{ opacity: 0.5 }}> · </span>
                <span>{fmtTokens(tokenTotal)} tok</span>
              </div>
              <button
                type="button"
                onClick={() => send()}
                disabled={sending || !input.trim()}
                className="text-[10px] uppercase tracking-[0.18em] px-3 py-1"
                style={{
                  border: '1px solid var(--rule)',
                  background: sending || !input.trim() ? 'transparent' : 'var(--btn-bg)',
                  color: sending || !input.trim() ? 'var(--muted)' : 'var(--btn-fg)',
                  cursor: sending || !input.trim() ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                }}
              >
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Bubble({ role, content, pending }) {
  const isUser = role === 'user';
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        marginBottom: 8,
      }}
    >
      <div
        style={{
          maxWidth: '85%',
          padding: '6px 9px',
          border: '1px solid var(--rule)',
          background: isUser ? 'var(--btn-bg)' : 'var(--sunken, transparent)',
          color: isUser ? 'var(--btn-fg)' : 'var(--ink)',
          fontSize: 12,
          lineHeight: 1.45,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          opacity: pending ? 0.6 : 1,
        }}
      >
        <div className="text-[8px] uppercase tracking-[0.22em] mb-0.5" style={{ opacity: 0.55 }}>
          {isUser ? 'You' : 'Assistant'}
        </div>
        {content}
      </div>
    </div>
  );
}

export default AiChatWidget;
